import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, ShieldCheck, KeyRound, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "manager", "owner"];
const ROLE_META = {
  admin: { label: "Admin", cls: "bg-blue-100 text-blue-700" },
  manager: { label: "Manager", cls: "bg-teal-100 text-teal-700" },
  owner: { label: "Owner", cls: "bg-amber-100 text-amber-700" },
};
const empty = { name: "", email: "", password: "", role: "owner" };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };

function apiError(e) {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : (Array.isArray(d) ? d.map((x) => x.msg).join(" ") : e.message);
}

export default function UsersMaster() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [resetFor, setResetFor] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [deleteFor, setDeleteFor] = useState(null);

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields required");
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success(`${u.name} → ${role}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doReset = async () => {
    if (newPass.length < 6) return toast.error("Password must be 6+ chars");
    try { await api.post(`/users/${resetFor.id}/reset-password`, { password: newPass }); toast.success("Password reset"); setResetFor(null); setNewPass(""); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/users/${deleteFor.id}`); toast.success("User deleted"); setDeleteFor(null); load(); }
    catch (e) { toast.error(apiError(e)); setDeleteFor(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy flex items-center gap-2" data-testid="users-title">
            <ShieldCheck className="h-6 w-6 text-ccc-blue" /> User Master
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage team accounts, roles and access · {users.length} users</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="bg-ccc-blue hover:bg-ccc-blue2 text-white"><Plus className="h-4 w-4 mr-1.5" /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label>Name</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Temporary password</Label><Input data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_META[r].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button data-testid="user-save-button" onClick={create} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Create user</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-100">
                <th className="py-3 px-5">User</th>
                <th className="py-3 px-4">Role</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Joined</th>
                <th className="py-3 px-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === user?.id;
                return (
                  <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`user-row-${u.email}`}>
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-ccc-navy text-white flex items-center justify-center text-sm font-semibold">{(u.name || "U").slice(0, 1)}</div>
                        <div>
                          <p className="font-medium text-ccc-navy">{u.name} {isSelf && <span className="text-[10px] text-ccc-blue">(you)</span>}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={isSelf}>
                        <SelectTrigger className="w-32 h-9" data-testid={`role-select-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_META[r].label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} disabled={isSelf} data-testid={`active-switch-${u.email}`} />
                        <Badge className={`border-0 ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.is_active ? "Active" : "Inactive"}</Badge>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-slate-500">{fmtDate(u.created_at)}</td>
                    <td className="py-3.5 px-5">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="outline" size="sm" className="h-8" data-testid={`reset-pass-${u.email}`} onClick={() => { setResetFor(u); setNewPass(""); }}>
                          <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 text-ccc-rose hover:text-rose-700 hover:bg-rose-50" disabled={isSelf} data-testid={`delete-user-${u.email}`} onClick={() => setDeleteFor(u)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset password dialog */}
      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Set a new password for <b className="text-ccc-navy">{resetFor?.name}</b>.</p>
          <Input data-testid="reset-password-input" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="New password" className="mt-1" />
          <DialogFooter><Button data-testid="reset-password-submit" onClick={doReset} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Update password</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes <b>{deleteFor?.name}</b> ({deleteFor?.email}). This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={doDelete} className="bg-ccc-rose hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
